import { cn } from "@/lib/utils";
import { WhatsappLogo, EnvelopeSimple, ChatText, PhoneCall } from "@phosphor-icons/react";

export const CHANNELS = ["whatsapp", "sms", "email", "ivr"];
export const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", Icon: WhatsappLogo, color: "text-emerald-600", bg: "bg-emerald-100 text-emerald-800" },
  sms: { label: "SMS", Icon: ChatText, color: "text-blue-600", bg: "bg-blue-100 text-blue-800" },
  email: { label: "Email", Icon: EnvelopeSimple, color: "text-violet-600", bg: "bg-violet-100 text-violet-800" },
  ivr: { label: "IVR / Voice", Icon: PhoneCall, color: "text-orange-600", bg: "bg-orange-100 text-orange-800" },
};

export function ChannelIcon({ channel, size = 16 }) {
  const m = CHANNEL_META[channel] || CHANNEL_META.sms;
  const I = m.Icon;
  return <I size={size} weight="bold" className={m.color} />;
}

export function ChannelBadge({ channel }) {
  const m = CHANNEL_META[channel] || { label: channel, Icon: ChatText, bg: "bg-secondary" };
  const I = m.Icon;
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold rounded-sm", m.bg)}>
      <I size={13} weight="bold" /> {m.label}
    </span>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 border-b border-border pb-4">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function Kpi({ label, value, sub, accent = "text-foreground", testId }) {
  return (
    <div className="border border-border bg-card p-4 h-full" data-testid={testId}>
      <div className="label-caps">{label}</div>
      <div className={cn("text-2xl md:text-3xl font-black font-heading tracking-tight mt-2 mono", accent)}>
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

const bucketColors = {
  Current: "bg-emerald-100 text-emerald-800",
  "1-30": "bg-blue-100 text-blue-800",
  "31-60": "bg-amber-100 text-amber-800",
  "61-90": "bg-orange-100 text-orange-800",
  "90+": "bg-red-100 text-red-800",
  Unknown: "bg-slate-200 text-slate-600",
};

export function BucketBadge({ bucket }) {
  return (
    <span className={cn("inline-block px-2 py-0.5 text-xs font-semibold rounded-sm mono", bucketColors[bucket] || "bg-secondary text-secondary-foreground")}>
      {bucket || "—"}
    </span>
  );
}

const statusColors = {
  active: "bg-blue-100 text-blue-800",
  closed: "bg-emerald-100 text-emerald-800",
  settled: "bg-teal-100 text-teal-800",
  ptp: "bg-violet-100 text-violet-800",
  suspended: "bg-red-100 text-red-800",
  invited: "bg-amber-100 text-amber-800",
  pending: "bg-amber-100 text-amber-800",
  pending_manager: "bg-amber-100 text-amber-800",
  pending_admin: "bg-orange-100 text-orange-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-800",
  countered: "bg-violet-100 text-violet-800",
  sent: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
  read: "bg-teal-100 text-teal-800",
  opened: "bg-teal-100 text-teal-800",
  answered: "bg-emerald-100 text-emerald-800",
  missed: "bg-amber-100 text-amber-800",
  scheduled: "bg-violet-100 text-violet-800",
  failed: "bg-red-100 text-red-800",
};

export function StatusBadge({ status }) {
  const label = (status || "").replace(/_/g, " ");
  return (
    <span className={cn("inline-block px-2 py-0.5 text-xs font-semibold rounded-sm capitalize", statusColors[status] || "bg-secondary text-secondary-foreground")}>
      {label}
    </span>
  );
}

export function Empty({ text = "No records found" }) {
  return <div className="text-center py-16 text-sm text-muted-foreground border border-dashed border-border">{text}</div>;
}

export function AiBadge({ score, band }) {
  if (score == null) return <span className="text-xs text-muted-foreground">—</span>;
  const cls = band === "High" ? "bg-emerald-100 text-emerald-700" : band === "Medium" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
  return <span className={cn("inline-block px-1.5 py-0.5 text-[11px] font-bold rounded-sm mono", cls)} title={`AI likelihood to pay (${band})`}>{score}</span>;
}
